import styled from '@emotion/styled';
import { Region } from '@kaizenplatform/hugot-entity';
import { Meta } from '@kaizenplatform/hugot-entity/dist/entity/DynamicSchemaDefinition/IDynamicSchemaSection';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { createElement, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import {
  metaToMaterialTypeOption,
  recommendSearchTexts,
  SearchAssetResult,
  SearchAssetService,
} from '../../../../entity/SearchAsset';
import { useSearchAsset } from '../../../../hooks/SearchAsset/useSearchAsset';
import { AudioPreview } from './AudioPreview';
import { Credit as _Credit } from './Credit';
import { MaterialTypeTab } from './MaterialTypeTab';
import { Preview } from './Preview';
import { SearchData } from './SearchData';
import { SearchForm } from './SearchForm';
import { SubTypeTab } from './SubTypeTab';

export type Props = {
  meta: Meta;
  region: Region;
  selectedSearchAsset: SearchAssetResult[number] | null;
  setSelectedSearchAsset: Dispatch<SetStateAction<SearchAssetResult[number] | null>>;
};

export const PreparedMaterialSelector = ({ meta, region, selectedSearchAsset, setSelectedSearchAsset }: Props) => {
  const materialTypeOption = metaToMaterialTypeOption(meta);

  const [data, setData] = useState<SearchAssetResult>([]);
  const [searchText, setSearchText] = useState('');
  const [audioSearchText, setAudioSearchText] = useState('');
  const [isOpenPreview, setIsOpenPreview] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<SearchAssetResult[number] | null>(null);
  const [cursor, setCursor] = useState(1);
  const searchDataRef = useRef<HTMLDivElement>(null);

  const {
    searchApi: { searchResult, isLoading, doFetchApi },
    typeAndSubType: { materialType, subType, subTypes, handleMaterialTypeChange, handleSubTypeChange },
  } = useSearchAsset(materialTypeOption, region, setSelectedSearchAsset);

  useEffect(() => {
    if (materialType.name !== 'audio') {
      setData(searchResult);
    } else {
      setData(
        searchResult.filter(
          (item) =>
            subType.name === item.tempo &&
            item.fileName &&
            item.fileName.toLowerCase().match(audioSearchText.toLowerCase()),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult, audioSearchText, subType.index]);

  const scrollToTop = () => {
    if (searchDataRef != null && searchDataRef.current != null) {
      searchDataRef.current.scrollTo({ top: 0 });
    }
  };

  const resetSearch = () => {
    setSearchText('');
    setAudioSearchText('');
    setPreviewAsset(null);
    setCursor(1);
  };

  const searchAsset = (query: string) => {
    setCursor(1);
    setSelectedSearchAsset(null);
    setAudioSearchText('');
    doFetchApi({
      type: materialType.name,
      service: (subType.isService ? subType.name : null) as SearchAssetService | null,
      query: query,
      region,
      reset: true,
    });
  };

  const searchAssetNextCursor = () => {
    if (
      searchDataRef == null ||
      searchDataRef.current == null ||
      searchDataRef.current.scrollTop === 0 ||
      materialType.name === 'audio' ||
      isLoading
    ) {
      return;
    }

    const nextCursor = cursor + 1;
    setCursor(nextCursor);

    doFetchApi({
      type: materialType.name,
      service: (subType.isService ? subType.name : null) as SearchAssetService | null,
      query: searchText,
      page: nextCursor,
      region,
      reset: false,
    });
  };

  return (
    <Root>
      <Box marginBottom={4}>
        <MaterialTypeTab
          materialType={materialType}
          materialTypeOption={materialTypeOption}
          resetSearch={resetSearch}
          scrollToTop={scrollToTop}
          handleChange={handleMaterialTypeChange}
        />
      </Box>
      <GridWithoutTab container spacing={2}>
        <Grid item xs={3}>
          <SubTypeTab
            materialType={materialType}
            subType={subType}
            subTypes={subTypes}
            resetSearch={resetSearch}
            scrollToTop={scrollToTop}
            handleChange={handleSubTypeChange}
          />
        </Grid>
        <GridWithHeight item xs={9}>
          <Grid container spacing={3} justify={materialType.name !== 'audio' ? 'space-between' : 'flex-start'}>
            <Grid item>
              <SearchForm
                scrollToTop={scrollToTop}
                searchText={searchText}
                setSearchText={setSearchText}
                setAudioSearchText={setAudioSearchText}
                useApi={materialType.name !== 'audio'}
                searchAsset={searchAsset}
                recommendSearchTexts={recommendSearchTexts[region][materialType.name]}
              />
            </Grid>
            <AudioPreview previewAsset={previewAsset} />
            <Grid item>
              <Credit subType={subType} />
            </Grid>
          </Grid>
          <GridWithHeight item>
            <SearchData
              data={data}
              searchDataRef={searchDataRef}
              selectedSearchAsset={selectedSearchAsset}
              setSelectedSearchAsset={setSelectedSearchAsset}
              isLoading={isLoading}
              searchAssetNextCursor={searchAssetNextCursor}
              setIsOpenPreview={setIsOpenPreview}
              setPreviewAsset={setPreviewAsset}
            />
          </GridWithHeight>
        </GridWithHeight>
      </GridWithoutTab>
      <Preview
        materialType={materialType}
        isOpenPreview={isOpenPreview}
        setIsOpenPreview={setIsOpenPreview}
        previewAsset={previewAsset}
      />
    </Root>
  );
};

const Root = styled('div')`
  height: 80vh;
  max-height: 800px;
`;

const GridWithoutTab = styled(Grid)`
  height: calc(100% - 150px);
`;

const GridWithHeight = styled(Grid)`
  height: 100%;
`;

const Credit = styled(_Credit)`
  padding-top: 40px;
`;
